"use client";

import { useState, useEffect } from "react";
import { DocsLayout } from "@/components/cli/DocsLayout";
import { DocsBreadcrumb } from "@/components/cli/DocsBreadcrumb";
import { DocsTableOfContents, type TocItem } from "@/components/cli/DocsTableOfContents";
import { CodeBlock } from "@/components/cli/CodeBlock";
import type { DocsSidebarItem } from "@/components/cli/DocsSidebar";

// Sidebar navigation
const sidebarItems: DocsSidebarItem[] = [
  {
    title: "Get started",
    defaultOpen: true,
    items: [
      { title: "Installation", href: "/cli" },
      { title: "Authentication", href: "/cli/authentication" },
    ],
  },
  {
    title: "Usage",
    defaultOpen: true,
    items: [
      { title: "Claude Code", href: "/cli/usage" },
      { title: "Cursor", href: "/cli/usage/cursor", disabled: true },
      { title: "Kiro", href: "/cli/usage/kiro", disabled: true },
      { title: "Gemini", href: "/cli/usage/gemini", disabled: true },
      { title: "Copilot", href: "/cli/usage/copilot", disabled: true },
    ],
  },
];

// Table of contents items
const tocItems: TocItem[] = [
  { id: "commands", title: "Commands" },
  { id: "other-commands", title: "Other commands" },
  { id: "analysis-validation", title: "Analysis & Validation", level: 3 },
  { id: "setup-configuration", title: "Setup & Configuration", level: 3 },
  { id: "collaboration", title: "Collaboration", level: 3 },
  { id: "dependencies", title: "Dependencies", level: 3 },
  { id: "help", title: "Help", level: 3 },
  { id: "whats-next", title: "What's next?" },
];

export default function UsagePage() {
  const [activeTocId, setActiveTocId] = useState<string>(tocItems[0]?.id);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120;

      let currentSection = tocItems[0]?.id;

      for (const item of tocItems) {
        const element = document.getElementById(item.id);
        if (element) {
          const { offsetTop } = element;
          if (scrollPosition >= offsetTop) {
            currentSection = item.id;
          }
        }
      }

      setActiveTocId(currentSection);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <DocsLayout
      sidebar={sidebarItems}
      rightSidebar={
        <DocsTableOfContents
          items={tocItems}
          activeId={activeTocId}
          onItemClick={setActiveTocId}
        />
      }
    >
      {/* Breadcrumb */}
      <DocsBreadcrumb
        items={[
          { label: "CLI", href: "/cli" },
          { label: "Usage" },
          { label: "Claude Code" },
        ]}
        className="mb-6"
      />

      {/* Page Title */}
      <h1 className="text-3xl md:text-4xl font-bold text-text mb-4">
        Usage with Claude Code
      </h1>

      {/* Introduction */}
      <p className="text-lg text-text-secondary mb-6 leading-relaxed">
        Launch Claude Code and use Specledger commands to build your feature.
        Run these commands in sequence.
      </p>

      {/* Sync callout */}
      <div className="mb-8 p-4 bg-bg-alt rounded-xl">
        <p className="text-sm font-medium text-text mb-2">Want to sync with your team?</p>
        <p className="text-sm text-text-muted mb-3">
          Create a project on the web app first. Your specs, plans, and tasks will sync automatically when you commit.
        </p>
        <a
          href="https://app.specledger.io"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:underline"
        >
          Create project on web
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>

      {/* Commands */}
      <section id="commands" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-6">Commands</h2>

        <div className="space-y-6">
          <div>
            <p className="text-sm font-medium text-text-secondary mb-2">1. Describe your feature</p>
            <CodeBlock code="/specledger.specify" />
          </div>
          <div>
            <p className="text-sm font-medium text-text-secondary mb-2">2. Generate implementation plan</p>
            <CodeBlock code="/specledger.plan" />
          </div>
          <div>
            <p className="text-sm font-medium text-text-secondary mb-2">3. Create development tasks</p>
            <CodeBlock code="/specledger.tasks" />
          </div>
          <div>
            <p className="text-sm font-medium text-text-secondary mb-2">4. Implement the feature</p>
            <CodeBlock code="/specledger.implement" />
          </div>
        </div>

        <div className="mt-6 p-4 bg-bg-alt rounded-xl">
          <p className="text-sm font-medium text-text mb-2">Tips</p>
          <ul className="space-y-1.5 text-sm text-text-muted">
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              Describe your feature in natural language
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              Review each step before proceeding to the next
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              Works offline - login and create project on web to enable sync
            </li>
          </ul>
        </div>
      </section>

      {/* Other commands */}
      <section id="other-commands" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-6">Other commands</h2>

        {/* Analysis & Validation */}
        <h3 id="analysis-validation" className="text-lg font-semibold text-text mb-4">Analysis & Validation</h3>
        <div className="space-y-4 mb-8">
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.analyze</code>
              <span className="text-sm text-text-muted">- Cross-artifact consistency check across spec, plan, and tasks</span>
            </div>
          </div>
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.audit</code>
              <span className="text-sm text-text-muted">- Complete codebase audit with tech stack detection and dependency graphs</span>
            </div>
          </div>
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.clarify</code>
              <span className="text-sm text-text-muted">- Identify and resolve spec ambiguities via targeted questions</span>
            </div>
          </div>
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.checklist</code>
              <span className="text-sm text-text-muted">- Generate custom validation checklist for the feature</span>
            </div>
          </div>
        </div>

        {/* Setup & Configuration */}
        <h3 id="setup-configuration" className="text-lg font-semibold text-text mb-4">Setup & Configuration</h3>
        <div className="space-y-4 mb-8">
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.constitution</code>
              <span className="text-sm text-text-muted">- Define project principles and coding standards</span>
            </div>
          </div>
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.adopt</code>
              <span className="text-sm text-text-muted">- Create spec from existing branch or audit output</span>
            </div>
          </div>
        </div>

        {/* Collaboration */}
        <h3 id="collaboration" className="text-lg font-semibold text-text mb-4">Collaboration</h3>
        <div className="space-y-4 mb-8">
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.revise</code>
              <span className="text-sm text-text-muted">- Fetch and address review comments</span>
            </div>
          </div>
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.resume</code>
              <span className="text-sm text-text-muted">- Continue previous work from where you left off</span>
            </div>
          </div>
        </div>

        {/* Dependencies */}
        <h3 id="dependencies" className="text-lg font-semibold text-text mb-4">Dependencies</h3>
        <div className="space-y-4 mb-8">
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.add-deps</code>
              <span className="text-sm text-text-muted">- Add dependencies to the spec</span>
            </div>
          </div>
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.remove-deps</code>
              <span className="text-sm text-text-muted">- Remove dependencies from the spec</span>
            </div>
          </div>
        </div>

        {/* Help */}
        <h3 id="help" className="text-lg font-semibold text-text mb-4">Help</h3>
        <div className="space-y-4">
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <code className="text-sm font-mono text-primary">/specledger.help</code>
              <span className="text-sm text-text-muted">- Display all available commands with descriptions</span>
            </div>
          </div>
        </div>
      </section>

      {/* What's next */}
      <section id="whats-next" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-4">What&apos;s next?</h2>
        <p className="text-text-secondary">
          Commit your changes and continue building.
        </p>
      </section>
    </DocsLayout>
  );
}
